import type { ApplicationStatus, JobStatus } from '@core/types';
import type { JobSortColumn } from '@core/supabase/services/jobService';

/** Serializable filters for `applicationService.list` — must stay in sync with that API. */
export type ApplicationsListParams = {
  candidateId?: string;
  jobId?: string;
  clientId?: string;
  assignedTo?: string;
  status?: ApplicationStatus;
  limit?: number;
};

/** Serializable filters for `jobService.list` (paginated). */
export type JobsListParams = {
  clientId?: string;
  status?: JobStatus;
  search?: string;
  page: number;
  pageSize: number;
  sortBy?: JobSortColumn;
  sortAsc?: boolean;
};

/** Serializable filters for `candidateService.list` (paginated). */
export type CandidatesListParams = {
  search?: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortAsc?: boolean;
};

export const queryKeys = {
  applications: {
    all: (orgId: string) => ['applications', orgId] as const,
    list: (orgId: string, params: ApplicationsListParams) => ['applications', orgId, 'list', params] as const,
    /** Aggregated application counts per `job_id` for jobs list (derived from list API). */
    jobCounts: (orgId: string) => ['applications', orgId, 'jobCounts'] as const,
  },
  clients: {
    all: (orgId: string) => ['clients', orgId] as const,
    list: (orgId: string) => ['clients', orgId, 'list'] as const,
  },
  jobs: {
    all: (orgId: string) => ['jobs', orgId] as const,
    list: (orgId: string, params: JobsListParams) => ['jobs', orgId, 'list', params] as const,
  },
  candidates: {
    all: (orgId: string) => ['candidates', orgId] as const,
    list: (orgId: string, params: CandidatesListParams) => ['candidates', orgId, 'list', params] as const,
  },
  smartMatch: {
    all: ['smart-match'] as const,
    matchResults: (orgId: string) => ['smart-match', 'match-results', orgId] as const,
    matchResultsByJob: (orgId: string, jobId: string) => ['smart-match', 'match-results', orgId, 'job', jobId] as const,
    matchResultsByCandidate: (orgId: string, candidateId: string) => ['smart-match', 'match-results', orgId, 'candidate', candidateId] as const,
    matchKPIs: (orgId: string) => ['smart-match', 'kpis', orgId] as const,
    bulkSessions: (orgId: string) => ['smart-match', 'bulk-sessions', orgId] as const,
    /** Ephemeral Pinecone-backed prompt search — scoped per pipelineId so
     *  each Smart Match thread has its own cached result set. */
    promptSearch: (orgId: string, pipelineId: string) =>
      ['smart-match', 'prompt-search', orgId, pipelineId] as const,
    /** N×M multi-JD search — one cache bucket per pipelineId + jd-hash. */
    multiJdSearch: (orgId: string, pipelineId: string, jdSignature: string) =>
      ['smart-match', 'multi-jd-search', orgId, pipelineId, jdSignature] as const,
    /**
     * Historical top-jobs-for-candidate lookup, batched by candidate
     * set. Used by the MatchCard `Also a fit for` footer when the
     * current search is single-JD (no live alternates).
     */
    altJobsForCandidates: (orgId: string, candidateIdSignature: string) =>
      ['smart-match', 'alt-jobs', orgId, candidateIdSignature] as const,
  },
  library: {
    all: (orgId: string) => ['library', orgId] as const,
    sources: (orgId: string) => ['library', orgId, 'sources'] as const,
  },
  sequences: {
    all: ['sequences'] as const,
    recipients: (sequenceId: string) => ['sequences', sequenceId, 'recipients'] as const,
  },
  interviewTemplates: {
    all: (orgId: string) => ['interviewTemplates', orgId] as const,
    list: (orgId: string) => ['interviewTemplates', orgId, 'list'] as const,
  },
  careers: {
    all: (orgId: string) => ['careers', orgId] as const,
    brand: (orgId: string) => ['careers', 'brand', orgId] as const,
  },
  org: {
    all: (orgId: string) => ['org', orgId] as const,
    /** Phase 8 · C-38 — automation / position defaults (default_tat_days, …). */
    defaults: (orgId: string) => ['org', 'defaults', orgId] as const,
  },
  reportsTat: {
    all: (orgId: string) => ['reportsTat', orgId] as const,
    byPosition: (orgId: string, rangeKey: string, from?: string, to?: string) =>
      ['reportsTat', orgId, 'byPosition', rangeKey, from ?? '', to ?? ''] as const,
    byIndividual: (orgId: string, rangeKey: string, from?: string, to?: string) =>
      ['reportsTat', orgId, 'byIndividual', rangeKey, from ?? '', to ?? ''] as const,
    byHierarchy: (orgId: string, rangeKey: string, from?: string, to?: string) =>
      ['reportsTat', orgId, 'byHierarchy', rangeKey, from ?? '', to ?? ''] as const,
  },
  /**
   * Phase 8 · C-47 — team member profile pages.
   *
   * Keys include `viewerId` so React Query caches are NEVER shared
   * across viewers. This preserves the RBAC guarantee in rbac.ts:
   * flipping orgs / impersonating a different user invalidates every
   * cached profile read automatically.
   */
  teamMemberProfile: {
    all: (orgId: string) => ['teamMemberProfile', orgId] as const,
    profile: (orgId: string, viewerId: string, targetId: string) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'profile'] as const,
    requisitions: (orgId: string, viewerId: string, targetId: string, status?: string) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'requisitions', status ?? ''] as const,
    candidates: (orgId: string, viewerId: string, targetId: string, limit?: number) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'candidates', limit ?? 100] as const,
    applications: (orgId: string, viewerId: string, targetId: string, stage?: string) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'applications', stage ?? ''] as const,
    interviews: (orgId: string, viewerId: string, targetId: string) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'interviews'] as const,
    emails: (orgId: string, viewerId: string, targetId: string, limit?: number) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'emails', limit ?? 50] as const,
    activity: (orgId: string, viewerId: string, targetId: string, limit?: number) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'activity', limit ?? 50] as const,
    throughput: (orgId: string, viewerId: string, targetId: string) =>
      ['teamMemberProfile', orgId, viewerId, targetId, 'throughput'] as const,
  },
  reportsTeam: {
    all: (orgId: string) => ['reportsTeam', orgId] as const,
    members: (orgId: string, rangeKey: string, from?: string, to?: string) =>
      ['reportsTeam', orgId, 'members', rangeKey, from ?? '', to ?? ''] as const,
    orgKpis: (orgId: string, rangeKey: string, from?: string, to?: string) =>
      ['reportsTeam', orgId, 'orgKpis', rangeKey, from ?? '', to ?? ''] as const,
    memberActivity: (orgId: string, memberId: string, rangeKey: string, from?: string, to?: string) =>
      ['reportsTeam', orgId, 'member', memberId, 'activity', rangeKey, from ?? '', to ?? ''] as const,
    memberSparkline: (orgId: string, memberId: string) =>
      ['reportsTeam', orgId, 'member', memberId, 'sparkline'] as const,
  },
} as const;
